import { useState, useEffect } from 'react';
import { useAuthStore, apiClient } from '@dukene/shared';
import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  email: string;
}

interface NewContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function NewContactModal({ isOpen, onClose, onSuccess }: NewContactModalProps) {
  const { user } = useAuthStore();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [name, setName] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch customers
  const { data: customersResponse } = useQuery<any>({
    queryKey: ['merchant-customers', user?.storeId],
    queryFn: async () => {
      // Fetch active customers
      return apiClient.get<any>(`/customers/merchant?status=ACTIVE`);
    },
    enabled: isOpen && !!user?.storeId,
  });

  const customersList: Customer[] = customersResponse?.customers || (Array.isArray(customersResponse) ? customersResponse : []);

  // When a customer is selected, auto-fill the phone and name
  useEffect(() => {
    if (selectedCustomerId) {
      const cust = customersList.find(c => c.id === selectedCustomerId);
      if (cust) {
        setName(`${cust.firstName} ${cust.lastName}`.trim());
        if (cust.phone) setPhoneNumber(cust.phone);
      }
    }
  }, [selectedCustomerId, customersList]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber) {
      setError('Phone number is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await apiClient.post(`/whatsapp/contacts/${user?.storeId}`, {
        phoneNumber,
        name,
        customerId: selectedCustomerId || undefined
      });
      
      onSuccess();
      onClose();
      // Reset state
      setPhoneNumber('');
      setName('');
      setSelectedCustomerId('');
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.error || err.message || 'Failed to create contact');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">New Contact</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 rounded-md">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Link Existing Customer (Optional)
            </label>
            <select
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- None --</option>
              {customersList.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.firstName} {customer.lastName} ({customer.phone || 'No phone'})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. John Doe"
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="e.g. 254712345678"
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">Include country code (e.g. 254...)</p>
          </div>

          <div className="pt-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
